import React, { useEffect, useState, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import {
  Container, Paper, Typography, Card, 
  CardContent, Button, TextField, Alert,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, 
  IconButton, Box, CircularProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';

interface Agent {
  id: string;
  status: string;
  balance: number;
  actions: number;
  successfulActions: number;
  failedActions: number;
  successRate: number;
  lastActivity: string;
  address: string;
  history: Array<{
    timestamp: string;
    decision: {
      action?: {
        type: string;
        params: any;
      };
      reason: string;
      confidence: number;
    };
    result?: any;
    error?: string;
  }>;
}

function App() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [newAgentId, setNewAgentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [balanceHistory, setBalanceHistory] = useState<any[]>([]);

  const API_BASE = process.env.NODE_ENV === 'production' 
    ? 'https://agentic-wallet-api.onrender.com/api'
    : 'http://localhost:3000/api';

  const loadAgents = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/agents`);
      const data = await response.json();
      setAgents(data.agents || []);
      
      setBalanceHistory(prev => {
        const newEntry: any = {
          timestamp: new Date().toLocaleTimeString()
        };
        (data.agents || []).forEach((agent: Agent) => {
          newEntry[agent.id] = agent.balance;
        });
        const newHistory = [...prev, newEntry];
        return newHistory.slice(-20);
      });
      
      setError(null);
    } catch (err) {
      setError('Failed to fetch agents');
    }
  }, [API_BASE]);

  useEffect(() => {
    loadAgents();
    const interval = setInterval(loadAgents, 5000);
    return () => clearInterval(interval);
  }, [loadAgents]);

  const handleCreateAgent = async () => {
    if (!newAgentId.trim()) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          command: 'create-agent', 
          agentId: newAgentId, 
          amount: 0.1 
        })
      });
      const result = await response.json();
      if (result.success) {
        setSuccess(`Agent ${newAgentId} created`);
        setNewAgentId('');
        await loadAgents();
      } else {
        setError(result.error || 'Failed to create agent');
      }
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleRunCycle = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          command: 'run-cycle'  // Fixed: Changed from 'run-multiple-cycles'
        })
      });
      const result = await response.json();
      if (result.success) {
        setSuccess('Cycle completed - agents are thinking!');
        await loadAgents();
      } else {
        setError(result.error || 'Failed to run cycle');
      }
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleStopAgent = async (agentId: string) => {
    try {
      const response = await fetch(`${API_BASE}/agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          command: 'stop-agent', 
          agentId 
        })
      });
      const result = await response.json();
      if (result.success) {
        setSuccess(`Agent ${agentId} stopped`);
        await loadAgents();
      } else {
        setError(result.error || 'Failed to stop agent');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getActionTypeDisplay = (decision: any) => {
    if (!decision || !decision.action) return 'THINKING';
    return decision.action.type || 'DECIDING';
  };

  const getActionReason = (decision: any) => {
    if (!decision) return 'No data';
    return decision.reason || 'Analyzing market conditions';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Paper sx={{ mb: 4, bgcolor: '#000', color: '#fff', p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">🤖 Agentic Wallet • Solana Devnet</Typography>
          <IconButton onClick={loadAgents} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Control Panel */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>Control Panel</Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            sx={{ flexGrow: 1, minWidth: '250px' }}
            label="New Agent ID"
            variant="outlined"
            value={newAgentId}
            onChange={(e) => setNewAgentId(e.target.value)}
            placeholder="e.g., trading-bot-1"
            disabled={loading}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateAgent}
            disabled={loading || !newAgentId}
            startIcon={<AddIcon />}
            sx={{ height: '56px' }}
          >
            Create Agent
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleRunCycle}
            disabled={loading || agents.length === 0}
            startIcon={<PlayArrowIcon />}
            sx={{ height: '56px' }}
          >
            Run 1 Cycle
          </Button>
        </Box>
      </Paper>

      {/* Agent Cards */}
      <Typography variant="h5" gutterBottom>Active Agents ({agents.length})</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        {agents.map((agent) => (
          <Box key={agent.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' } }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">{agent.id}</Typography>
                  <Chip 
                    label={agent.status}
                    color={
                      agent.status === 'RUNNING' ? 'success' :
                      agent.status === 'IDLE' ? 'warning' :
                      agent.status === 'ERROR' ? 'error' : 'default'
                    }
                    size="small"
                  />
                </Box>

                <Box display="flex" gap={2} mb={2}>
                  <Box flex={1}>
                    <Typography color="text.secondary" variant="body2">Balance</Typography>
                    <Typography variant="h5" color="primary">
                      {agent.balance?.toFixed(4) || '0.0000'} SOL
                    </Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography color="text.secondary" variant="body2">Success Rate</Typography>
                    <Typography variant="h5" color={agent.successRate > 70 ? 'success.main' : 'warning.main'}>
                      {agent.successRate?.toFixed(1) || '0'}%
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" gap={2} mb={2}>
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">Actions</Typography>
                    <Typography variant="body2">{agent.actions || 0}</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">Successful</Typography>
                    <Typography variant="body2" color="success.main">{agent.successfulActions || 0}</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">Failed</Typography>
                    <Typography variant="body2" color="error.main">{agent.failedActions || 0}</Typography>
                  </Box>
                </Box>

                <Typography variant="caption" display="block" color="text.secondary">
                  Address: {agent.address?.slice(0, 6)}...{agent.address?.slice(-4)}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                  Last Active: {agent.lastActivity ? new Date(agent.lastActivity).toLocaleTimeString() : 'Never'}
                </Typography>

                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleStopAgent(agent.id)}
                  startIcon={<StopIcon />}
                  disabled={agent.status === 'STOPPED'}
                >
                  Stop Agent
                </Button>
              </CardContent>
            </Card>
          </Box>
        ))}
        {agents.length === 0 && (
          <Box width="100%">
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No agents yet. Create one to get started!</Typography>
            </Paper>
          </Box>
        )}
      </Box>

      {/* Balance Chart */}
      {balanceHistory.length > 0 && agents.length > 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>Agent Balances Over Time</Typography>
          <Box height={300}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={balanceHistory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" />
                <YAxis />
                <Tooltip />
                <Legend />
                {agents.map((agent, index) => (
                  <Line
                    key={agent.id}
                    type="monotone"
                    dataKey={agent.id}
                    stroke={`hsl(${index * 60}, 70%, 50%)`}
                    name={agent.id}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}

      {/* Live Activity Feed - NOW POPULATED! */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          🤖 Live Agent Decision Feed
          <Chip 
            label="Real-time AI decisions" 
            size="small" 
            color="primary" 
            sx={{ ml: 2 }}
          />
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>AI Reasoning</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.flatMap((agent) => 
                (agent.history || []).slice().reverse().map((tx, idx) => (
                  <TableRow key={`${agent.id}-${idx}`} hover>
                    <TableCell>
                      {tx.timestamp ? new Date(tx.timestamp).toLocaleTimeString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={agent.id} 
                        size="small" 
                        variant="outlined"
                        color={tx.error ? 'error' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getActionTypeDisplay(tx.decision)}
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tx.error ? 'Failed' : 'Success'}
                        color={tx.error ? 'error' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getActionReason(tx.decision)}
                        {tx.result?.signature && (
                          <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                            (Tx: {tx.result.signature.substring(0, 8)}...)
                          </Box>
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      No agents yet. Create one and run cycles to see AI decisions!
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {agents.length > 0 && agents.every(a => a.history.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      Click "Run 1 Cycle" to see your AI agents think and decide!
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Loading Overlay */}
      {loading && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999
        }}>
          <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography>Processing AI decisions...</Typography>
          </Paper>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          ⚡ Built for Superteam Earn Challenge • Solana Devnet • AI agents thinking every 5s
        </Typography>
      </Box>
    </Container>
  );
}

export default App;